import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

/**
 * PWA "Add to Home Screen" banner.
 * - Chrome/Android: uses beforeinstallprompt to show a native install button
 * - iOS Safari: shows manual instructions with a Share icon
 * - Only appears on mobile viewport, after 10s, once per 14 days (localStorage)
 * - Hidden on public quote pages so customers aren't prompted
 */
const DISMISS_KEY = "rq_install_dismissed_at";
const DISMISS_DAYS = 14;

function isMobile() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 900px)").matches
    || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
}

function isIOS() {
  if (typeof window === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || "") && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
}

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

export default function PwaInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    // Hide on public quote pages
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/q/")) return;
    if (!isMobile() || isStandalone() || recentlyDismissed()) return;

    let timer;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      timer = setTimeout(() => setVisible(true), 10_000);
    };

    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS Safari never fires beforeinstallprompt — show manual guide after 10s
    if (isIOS()) {
      timer = setTimeout(() => { setShowIos(true); setVisible(true); }, 10_000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice.catch(() => null);
    setDeferred(null);
    setVisible(false);
    if (choice?.outcome !== "accepted") dismiss();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed inset-x-3 bottom-3 z-50 sm:left-auto sm:right-4 sm:w-[380px]"
    >
      <div className="rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
        <div className="p-4 flex items-start gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#001F3F] flex items-center justify-center shrink-0">
            <span className="text-white font-black text-sm tracking-tight">R.</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-[#001F3F]">Install RivetQuote</div>
            <div className="text-xs text-[#64748B] mt-0.5">
              {showIos
                ? <>Tap <Share size={11} className="inline -mt-0.5" /> then <b>Add to Home Screen</b> to keep it one tap away.</>
                : "Quote from the pub, the van, or the job — one tap away on your home screen."}
            </div>
            <div className="mt-3 flex items-center gap-2">
              {!showIos && deferred && (
                <button
                  type="button"
                  onClick={install}
                  data-testid="pwa-install-btn"
                  className="h-9 px-3 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-semibold inline-flex items-center gap-1.5"
                >
                  <Download size={13} /> Install
                </button>
              )}
              <button
                type="button"
                onClick={dismiss}
                data-testid="pwa-install-dismiss"
                className="h-9 px-3 rounded-full border border-slate-200 text-[#64748B] text-xs font-semibold hover:bg-slate-50"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="text-[#94A3B8] hover:text-[#334155] shrink-0"
            aria-label="Close install banner"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
